import * as path from 'path';
import { defineConfig, UserConfig } from 'vite';
import vue from '@vitejs/plugin-vue';
import { createRouterPlugin } from '../plugins/RouterPlugin.js';
import { createEntryPlugin } from '../plugins/EntryPlugin.js';
import { createAssetPlugin } from '../plugins/AssetPlugin.js';
import { createDevToolsPlugin } from '../plugins/DevToolsPlugin.js';
import { createPugPlugin } from '../plugins/PugPlugin.js';
import { createMetaPlugin } from '../plugins/MetaPlugin.js';
import { ConfigLoader } from './ConfigLoader.js';

export async function createViteConfig(cwd: string): Promise<UserConfig> {
	// Load Frame configuration
	const configLoader = new ConfigLoader(cwd);
	const frameConfig = await configLoader.load();

	const srcPath = path.join(cwd, 'src');
	const publicPath = path.join(cwd, frameConfig.assets?.publicDir || 'public');
	const assetsPath = path.join(cwd, frameConfig.assets?.assetsDir || 'src/assets');
	const viewsPath = path.join(cwd, frameConfig.routing?.viewsDir || 'src/views');

	// Create Vue plugin first
	const vuePlugin = vue({
		template: {
			preprocessOptions: frameConfig.templates?.pug?.enabled !== false ? {
				pug: frameConfig.templates?.pug?.options || {}
			} : {}
		}
	});

	// Base Vite configuration
	const baseConfig: UserConfig = {
		// Root directory
		root: cwd,

		// Plugins
		plugins: [
			vuePlugin,
			createRouterPlugin(viewsPath),
			createEntryPlugin(cwd),
			createAssetPlugin(cwd, {
				publicDir: frameConfig.assets?.publicDir,
				assetsDir: frameConfig.assets?.assetsDir,
				generateHelpers: frameConfig.assets?.generateHelpers,
			}),
			createPugPlugin(cwd, frameConfig.templates?.pug || {}),
			createMetaPlugin(cwd, frameConfig.meta || {}),
			createDevToolsPlugin(cwd, frameConfig.devTools || {}),
			...(frameConfig.vite?.plugins || [])
		],

		// Entry point - use user's public/index.html or create virtual one
		build: {
			outDir: frameConfig.build?.outDir || 'dist',
			sourcemap: frameConfig.build?.sourcemap ?? true,
			minify: frameConfig.build?.minify ?? true,
			assetsDir: 'assets',
			rollupOptions: {
				output: {
					assetFileNames: (assetInfo) => {
						const extType = assetInfo.name?.split('.').at(-1);
						if (extType && /png|jpe?g|svg|gif|tiff|bmp|ico/i.test(extType)) {
							return `images/[name]-[hash][extname]`;
						}
						if (extType && /woff2?|ttf|otf|eot/i.test(extType)) {
							return `fonts/[name]-[hash][extname]`;
						}
						return `assets/[name]-[hash][extname]`;
					},
				},
			},
			...(frameConfig.vite?.build || {})
		},

		// Resolve configuration
		resolve: {
			alias: {
				'@': srcPath,
				'@views': viewsPath,
				'@assets': assetsPath,
				'#frame': path.join(srcPath, '.frame'),
			},
			...(frameConfig.vite?.resolve || {})
		},

		// Public directory
		publicDir: publicPath,

		// Server configuration - explicitly set the values first, then merge Vite overrides
		server: {
			port: frameConfig.dev?.port || 3000,
			host: frameConfig.dev?.host || 'localhost',
			open: frameConfig.dev?.open || false,
			hmr: frameConfig.devTools?.hmr !== false ? {
				overlay: frameConfig.devTools?.overlay !== false,
			} : false,
			watch: {
				ignored: frameConfig.dev?.ignore || ['**/@api/**'], // Configurable patterns to ignore in file watching
			},
			...(frameConfig.vite?.server || {})
		},

		// CSS configuration
		css: {
			devSourcemap: true,
			preprocessorOptions: {
				less: {
					javascriptEnabled: true,
					// Allow consuming projects to override LESS options
					...(frameConfig.vite?.css?.preprocessorOptions?.less || {})
				},
				scss: {
					// Allow consuming projects to override SCSS options
					...(frameConfig.vite?.css?.preprocessorOptions?.scss || {})
				}
			},
			postcss: {
				// This ensures PostCSS (required for Tailwind @apply) works
				// Consuming projects can provide their own postcss.config.js
				...(frameConfig.vite?.css?.postcss || {})
			}
		},

		// TypeScript configuration for better IDE support
		esbuild: {
			target: 'esnext',
		},

		// Define for development
		define: {
			__VUE_OPTIONS_API__: true,
			__VUE_PROD_DEVTOOLS__: false,
		},

		// Asset optimization
		assetsInclude: ['**/*.png', '**/*.jpg', '**/*.jpeg', '**/*.gif', '**/*.svg', '**/*.webp', '**/*.avif'],

		// Optimized dependencies
		optimizeDeps: {
			include: ['vue', 'vue-router', 'pinia'],
			exclude: ['@livx.cc/frame'],
		},
	};

	// Return the config object directly
	return baseConfig;
} 